import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Building2, ChevronDown, Eye, EyeOff, FlaskConical, Loader2, UserRound, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/context/AuthContext";
import { errorMessage } from "@/lib/api";
import { ProximaLogo } from "@/components/brand/ProximaLogo";
import { cn } from "@/lib/utils";

const HERO_IMG = "/brand/turbine-lift.jpg";

const DEMO_USERS = [
  { key: "kunde-admin", role: "Kunde-admin", org: "Vestlandskraft Småkraft AS", scope: "26 kraftverk · full tilgang", email: "kari.nordmann@vestlandskraft.no", password: "Portal2026!", icon: Users },
  { key: "bruker", role: "Bruker", org: "Vestlandskraft Småkraft AS", scope: "5 utvalgte kraftverk · lesetilgang", email: "ola.hansen@vestlandskraft.no", password: "Portal2026!", icon: UserRound },
  { key: "eier", role: "Eier", org: "Haugland Gard", scope: "1 kraftverk", email: "per.haugland@hauglandgard.no", password: "Portal2026!", icon: UserRound },
  { key: "proxima-admin", role: "Proxima-admin", org: "Proxima Hydrotech", scope: "Alle kunder · administrasjon", email: "admin@proxima.no", password: "Proxima2026!", icon: Building2 },
];

const FACTS = [
  { value: "70+", label: "kraftverk i drift" },
  { value: "200+", label: "med serviceavtale" },
  { value: "24/7", label: "vakt og beredskap" },
];

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [demoBusy, setDemoBusy] = useState(null);
  const [error, setError] = useState(null);

  const submit = async (e, creds) => {
    e?.preventDefault?.();
    const em = creds?.email ?? email;
    const pw = creds?.password ?? password;
    if (!em || !pw) {
      setError("Fyll inn e-post og passord.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const user = await login(em.trim(), pw);
      toast.success(`Velkommen, ${user.name.split(" ")[0]}`);
      navigate(location.state?.from?.pathname || "/", { replace: true });
    } catch (err) {
      setError(errorMessage(err, "Innlogging feilet."));
    } finally {
      setBusy(false);
      setDemoBusy(null);
    }
  };

  const demoLogin = (u) => {
    setDemoBusy(u);
    setEmail(u.email);
    setPassword(u.password);
    submit(null, u);
  };

  return (
    <div className="min-h-screen bg-background lg:grid lg:grid-cols-[1.1fr_1fr]">
      {/* Brand panel */}
      <aside className="relative hidden overflow-hidden bg-[hsl(206_60%_9%)] text-white lg:flex lg:flex-col lg:justify-between lg:p-12 xl:p-14" data-testid="login-hero">
        <img src={HERO_IMG} alt="" className="absolute inset-0 h-full w-full object-cover object-[center_30%] opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-[hsl(206_60%_8%)] via-[hsl(206_60%_8%/0.6)] to-[hsl(206_60%_8%/0.25)]" aria-hidden="true" />
        <div className="noise-overlay" />
        <div className="relative">
          <ProximaLogo tone="onDark" tagline="Hydrotech" className="text-[20px]" />
        </div>
        <div className="relative max-w-xl">
          <div className="mono text-[11px] uppercase tracking-[0.12em] text-brand-cyan">HydroHub · Kundeportal</div>
          <h2 className="mt-4 text-[40px] font-semibold leading-[1.04] tracking-[-0.03em] xl:text-[50px]">
            Full oversikt over
            <br />
            dine kraftverk.
          </h2>
          <p className="mt-5 max-w-md text-[14.5px] leading-relaxed text-white/70">Produksjon, vannføring, spotpris og hendelser – samlet i én rolig og lesbar portal, bygget på driftsdataene fra Proximas driftssentral.</p>
          <dl className="mt-10 grid grid-cols-3 gap-6 border-t border-white/15 pt-6">
            {FACTS.map((f) => (
              <div key={f.value}>
                <dt className="numeral text-[26px] text-white">{f.value}</dt>
                <dd className="mt-1.5 text-xs leading-snug text-white/60">{f.label}</dd>
              </div>
            ))}
          </dl>
        </div>
      </aside>

      {/* Form panel */}
      <main className="relative flex min-h-screen flex-col px-6 py-6 sm:px-12 lg:min-h-0 lg:py-8">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-72" aria-hidden="true" style={{ background: "radial-gradient(600px 240px at 70% -30%, hsl(var(--primary) / 0.08), transparent 65%)" }} />
        <div className="relative flex items-center justify-between">
          <ProximaLogo tagline="HydroHub" className="text-[15px]" />
        </div>

        <div className="relative flex flex-1 items-center justify-center py-10">
          <div className="w-full max-w-[380px] animate-fade-up">
            <h1 className="text-[26px] font-semibold leading-tight tracking-[-0.025em]">Logg inn</h1>
            <p className="mt-1.5 text-[13.5px] leading-relaxed text-muted-foreground">Bruk e-post og passord du har fått fra Proxima.</p>

            <form onSubmit={submit} className="mt-7 space-y-4" data-testid="login-form" noValidate>
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-[12.5px] font-medium">
                  E-post
                </Label>
                <Input id="email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="navn@selskap.no" className="h-10 text-[14px]" data-testid="login-email-input" />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-[12.5px] font-medium">
                    Passord
                  </Label>
                  <span className="text-[11.5px] text-muted-foreground">Glemt passord? Kontakt Proxima.</span>
                </div>
                <div className="relative">
                  <Input id="password" type={show ? "text" : "password"} autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-10 pr-10 text-[14px]" data-testid="login-password-input" />
                  <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-[5px] p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground" aria-label={show ? "Skjul passord" : "Vis passord"} data-testid="login-toggle-password">
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              {error ? (
                <p className="rounded-md border border-destructive/30 px-3 py-2 text-[13px] text-destructive" role="alert" data-testid="login-error">
                  {error}
                </p>
              ) : null}
              <Button type="submit" size="lg" className="w-full font-semibold" disabled={busy} data-testid="login-submit-button">
                {busy && !demoBusy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
                Logg inn
              </Button>
            </form>

            <div className="mt-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" variant="outline" size="lg" className="w-full justify-between font-medium" disabled={busy} data-testid="login-testaccount-button">
                    <span className="inline-flex items-center gap-2">
                      {demoBusy ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-hidden="true" /> : <FlaskConical className="h-4 w-4 text-muted-foreground" aria-hidden="true" />}
                      {demoBusy ? `Logger inn som ${demoBusy.role} …` : "Utforsk med testkonto"}
                    </span>
                    <ChevronDown className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" sideOffset={6} className="w-[var(--radix-dropdown-menu-trigger-width)] p-1" data-testid="login-testaccount-menu">
                  <DropdownMenuLabel className="label px-2 py-1.5">Velg rolle · alle data er fiktive</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {DEMO_USERS.map((u) => {
                    const Icon = u.icon;
                    return (
                      <DropdownMenuItem key={u.key} onSelect={() => demoLogin(u)} className="cursor-pointer gap-3 px-2 py-2" data-testid={`login-testaccount-item-${u.key}`}>
                        <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border bg-muted text-muted-foreground")}>
                          <Icon className="h-4 w-4" aria-hidden="true" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-[13px] font-medium">{u.role}</span>
                          <span className="block truncate text-[11.5px] text-muted-foreground">
                            {u.org} · {u.scope}
                          </span>
                        </span>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <p className="mono mt-8 text-[10.5px] uppercase leading-relaxed tracking-[0.06em] text-muted-foreground">Kun lesetilgang · ingen styring av anlegg fra HydroHub</p>
          </div>
        </div>

        <footer className="relative flex flex-wrap items-center justify-between gap-2 text-[11px] text-muted-foreground">
          <span>© {new Date().getFullYear()} Proxima Hydrotech AS</span>
          <a href="https://proximahydrotech.no" target="_blank" rel="noreferrer" className="transition-colors hover:text-foreground">
            proximahydrotech.no
          </a>
        </footer>
      </main>
    </div>
  );
}
